import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel
} from "@/components/ui/dropdown-menu";
import {
  ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Search, MoreHorizontal, ExternalLink, EyeOff, Eye,
  Trash2, Download, ArrowUpDown, ArrowUp, ArrowDown, X, Loader2, RefreshCw, FlaskConical, Sparkles, Linkedin, Landmark, Send
} from "lucide-react";
import { toast } from "sonner";
import api, { exportUrl, safeExternalUrl } from "@/lib/api";
import { DomainDetailDialog } from "@/components/DomainDetailDialog";
import { FilterBuilder, PRESETS } from "@/components/FilterBuilder";
import { useAppContext } from "@/lib/AppContext";

const STATUS_COLORS = {
  pending: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30",
  crawling: "bg-cyan-500/15 text-cyan-400 border-cyan-500/30",
  done: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  error: "bg-red-500/15 text-red-400 border-red-500/30",
  rejected: "bg-orange-500/15 text-orange-400 border-orange-500/30",
  blocked: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  redirect: "bg-sky-500/15 text-sky-400 border-sky-500/30",
};

const SUPPORT_BADGE = {
  eol: { label: "EOL", cls: "bg-red-500/15 text-red-400 border-red-500/30" },
  eol_soon: { label: "EOL bald", cls: "bg-amber-500/15 text-amber-400 border-amber-500/30" },
  supported: { label: "aktuell", cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" },
};

const COST_PER_DOMAIN_USD = 0.0004;
const DEFAULT_PER_PAGE = 100;
const MAX_PAGES = 50; // deeper pages: refine the filter or export (matches backend MAX_PAGE)

// Per-user prefs (server-side, /api/me/prefs). The Domains table state follows the user
// across refreshes and devices; the Core team picker remembers the last assignee.
const TABLE_PREF_KEY = "domains.table";
const ASSIGNEE_PREF_KEY = "core.assignee";
const DEFAULT_TABLE_PREF = {
  conditions: [], searchInput: "", showHidden: false, showProblems: false,
  opened: "", sortBy: "created_at", sortOrder: "desc", perPage: DEFAULT_PER_PAGE,
};

const CMS_COLORS = {
  Drupal: "bg-blue-500/15 text-blue-400 border-blue-500/30",
  WordPress: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  TYPO3: "bg-purple-500/15 text-purple-400 border-purple-500/30",
  Contao: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  Shopify: "bg-pink-500/15 text-pink-400 border-pink-500/30",
  Joomla: "bg-orange-500/15 text-orange-400 border-orange-500/30",
  Magento: "bg-red-500/15 text-red-400 border-red-500/30",
  PrestaShop: "bg-fuchsia-500/15 text-fuchsia-400 border-fuchsia-500/30",
  Wix: "bg-yellow-500/15 text-yellow-400 border-yellow-500/30",
  Squarespace: "bg-slate-400/15 text-slate-300 border-slate-400/30",
  Webflow: "bg-indigo-500/15 text-indigo-400 border-indigo-500/30",
  Ghost: "bg-violet-500/15 text-violet-400 border-violet-500/30",
  Hugo: "bg-rose-500/15 text-rose-400 border-rose-500/30",
  Gatsby: "bg-purple-400/15 text-purple-300 border-purple-400/30",
  WooCommerce: "bg-violet-600/15 text-violet-400 border-violet-600/30",
  OpenCart: "bg-sky-500/15 text-sky-400 border-sky-500/30",
  BigCommerce: "bg-blue-400/15 text-blue-300 border-blue-400/30",
  "Salesforce Commerce Cloud": "bg-cyan-500/15 text-cyan-400 border-cyan-500/30",
  "Adobe Experience Manager": "bg-red-600/15 text-red-400 border-red-600/30",
  Sitecore: "bg-red-400/15 text-red-300 border-red-400/30",
  Kentico: "bg-orange-400/15 text-orange-300 border-orange-400/30",
  Umbraco: "bg-blue-600/15 text-blue-300 border-blue-600/30",
  "HubSpot CMS": "bg-orange-500/15 text-orange-400 border-orange-500/30",
  Jimdo: "bg-lime-500/15 text-lime-400 border-lime-500/30",
  "IONOS MyWebsite": "bg-blue-500/15 text-blue-300 border-blue-500/30",
  "Strato Homepage-Baukasten": "bg-sky-400/15 text-sky-300 border-sky-400/30",
  "Backdrop CMS": "bg-blue-400/15 text-blue-300 border-blue-400/30",
  Laravel: "bg-red-500/15 text-red-400 border-red-500/30",
  "Next.js": "bg-zinc-300/15 text-zinc-200 border-zinc-300/30",
  "Nuxt.js": "bg-emerald-600/15 text-emerald-300 border-emerald-600/30",
  Angular: "bg-red-600/15 text-red-300 border-red-600/30",
  "Vue.js": "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  unknown: "bg-zinc-500/15 text-zinc-500 border-zinc-500/30",
};

const SupportBadge = ({ status }) => {
  const b = SUPPORT_BADGE[status];
  return b ? <Badge className={`text-[9px] px-1.5 ${b.cls}`}>{b.label}</Badge> : null;
};

export const drupalLabel = (d) => {
  const dr = d.drupal || {};
  if (dr.version) return `Drupal ${dr.version}`;
  if (dr.version_minor) return `Drupal ${dr.version_minor}`;
  if (dr.major) return `Drupal ${dr.major}`;
  if (dr.major_hint) return `Drupal ${dr.major_hint}`;
  return d.cms_major;
};

const LeadScore = ({ score }) => {
  if (score == null) return <span className="text-zinc-700 text-[10px]">–</span>;
  const cls = score >= 60 ? "bg-emerald-500/15 text-emerald-300" : score >= 30 ? "bg-amber-500/15 text-amber-200" : "bg-zinc-800 text-zinc-400";
  return <span className={`inline-block min-w-[2rem] text-center font-mono text-[11px] font-semibold rounded-sm px-1.5 py-0.5 ${cls}`}>{score}</span>;
};

const CORE_STATUS = {
  active: { label: "In Core", cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" },
  exists: { label: "Existiert", cls: "bg-amber-500/15 text-amber-400 border-amber-500/30" },
  archived: { label: "Archiviert", cls: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30" },
  missing: { label: "Entfernt", cls: "bg-red-500/15 text-red-400 border-red-500/30" },
};

const CoreBadge = ({ link }) => {
  if (!link) return <span className="text-zinc-700 text-[10px]">–</span>;
  const s = CORE_STATUS[link.status] || { label: link.status, cls: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30" };
  return <Badge className={`text-[9px] px-1.5 ${s.cls}`}>{s.label}</Badge>;
};

const fmt = (n) => Number(n || 0).toLocaleString("de-DE");

const relTime = (iso) => {
  if (!iso) return "";
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return "gerade eben";
  const m = Math.floor(s / 60);
  if (m < 60) return `vor ${m} Min.`;
  const h = Math.floor(m / 60);
  if (h < 24) return `vor ${h} Std.`;
  const d = Math.floor(h / 24);
  if (d < 7) return `vor ${d} Tag${d === 1 ? "" : "en"}`;
  const w = Math.floor(d / 7);
  if (w < 5) return `vor ${w} Woche${w === 1 ? "" : "n"}`;
  return new Date(iso).toLocaleDateString("de-DE");
};

// "Me" in the Core team list: the crawler username maps to the Core user with the same
// local part of the email (CRAWLER_LINK_USERS on the Core side), else fall back to the first.
const defaultAssigneeEmail = (team, username) => {
  if (!team?.length) return "";
  const me = team.find((m) => (m.email || "").split("@")[0].toLowerCase() === (username || "").toLowerCase());
  return (me || team[0]).email || "";
};

function useDebounced(value, ms) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export const DomainsTable = ({ refreshKey, onRefresh, role = "viewer", currentUser }) => {
  const canWrite = role !== "viewer";
  const isAdmin = role === "admin";
  const currentUsername = currentUser?.username || "";
  const { researchActive, refreshNow } = useAppContext();

  const [schema, setSchema] = useState(null);
  const [conditions, setConditions] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const search = useDebounced(searchInput.trim(), 400);
  const [showHidden, setShowHidden] = useState(false);
  const [showProblems, setShowProblems] = useState(false);
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);
  const [page, setPage] = useState(1);

  const [domains, setDomains] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  // total: null while counting or when the filter is too broad to count exactly
  const [total, setTotal] = useState(null);
  const [totalCapped, setTotalCapped] = useState(false);
  const [counting, setCounting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [detailDomain, setDetailDomain] = useState(null);
  const [action, setAction] = useState(null);
  const [researchLimit, setResearchLimit] = useState(1000);
  const [busy, setBusy] = useState(false);
  const [coreLinkEnabled, setCoreLinkEnabled] = useState(false);
  const [coreLinks, setCoreLinks] = useState({});
  const [coreTeam, setCoreTeam] = useState([]);
  const [bulkCoreOpen, setBulkCoreOpen] = useState(false);
  const [bulkAssignee, setBulkAssignee] = useState("");
  const [bulkCount, setBulkCount] = useState(0);
  const [bulkSending, setBulkSending] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [opened, setOpened] = useState("");
  const [prefsLoaded, setPrefsLoaded] = useState(false);
  const [lastAssignee, setLastAssignee] = useState("");
  const requestId = useRef(0);
  const countId = useRef(0);
  const savedTablePrefRef = useRef(null);

  useEffect(() => {
    api.get("/filter-schema").then((r) => setSchema(r.data)).catch(() => {});
  }, [refreshKey]);

  useEffect(() => {
    api.get("/domains/core-link-enabled").then((r) => setCoreLinkEnabled(!!r.data.enabled)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!coreLinkEnabled || !canWrite) return;
    api.get("/domains/core-team").then((r) => setCoreTeam(r.data || [])).catch(() => {});
  }, [coreLinkEnabled, canWrite]);

  // Restore this user's saved table state (filters/sort/page size) once on mount.
  useEffect(() => {
    let cancelled = false;
    api.get(`/me/prefs/${TABLE_PREF_KEY}`)
      .then((r) => {
        if (cancelled) return;
        const v = r.data.value || {};
        savedTablePrefRef.current = JSON.stringify(v);
        setConditions(Array.isArray(v.conditions) ? v.conditions : []);
        setSearchInput(typeof v.searchInput === "string" ? v.searchInput : "");
        setShowHidden(!!v.showHidden);
        setShowProblems(!!v.showProblems);
        setOpened(v.opened === "opened" || v.opened === "not_opened" ? v.opened : "");
        setSortBy(v.sortBy || "created_at");
        setSortOrder(v.sortOrder === "asc" ? "asc" : "desc");
        setPerPage(Number(v.perPage) > 0 ? Number(v.perPage) : DEFAULT_PER_PAGE);
      })
      .catch(() => { savedTablePrefRef.current = JSON.stringify(DEFAULT_TABLE_PREF); })
      .finally(() => { if (!cancelled) setPrefsLoaded(true); });
    return () => { cancelled = true; };
  }, []);

  const tablePref = useMemo(() => ({
    conditions, searchInput, showHidden, showProblems, opened, sortBy, sortOrder, perPage,
  }), [conditions, searchInput, showHidden, showProblems, opened, sortBy, sortOrder, perPage]);

  // Debounce-save the table state after it is loaded, so the user's filters follow them.
  useEffect(() => {
    if (!prefsLoaded) return;
    const serialized = JSON.stringify(tablePref);
    if (serialized === savedTablePrefRef.current) return;
    const t = setTimeout(() => {
      savedTablePrefRef.current = serialized;
      api.put(`/me/prefs/${TABLE_PREF_KEY}`, { value: tablePref }).catch(() => {});
    }, 800);
    return () => clearTimeout(t);
  }, [tablePref, prefsLoaded]);

  // Last "Send to Core" assignee of this user (per-user prefs), for the team picker default.
  useEffect(() => {
    if (!coreLinkEnabled || !canWrite) return;
    api.get(`/me/prefs/${ASSIGNEE_PREF_KEY}`)
      .then((r) => setLastAssignee(r.data.value || ""))
      .catch(() => setLastAssignee(""));
  }, [coreLinkEnabled, canWrite]);

  const saveAssignee = useCallback((email) => {
    const value = email || "";
    setLastAssignee(value);
    api.put(`/me/prefs/${ASSIGNEE_PREF_KEY}`, { value }).catch(() => {});
  }, []);

  const resolveAssignee = useCallback((team) => {
    if (lastAssignee && team?.some((m) => m.email === lastAssignee)) return lastAssignee;
    return defaultAssigneeEmail(team, currentUsername);
  }, [lastAssignee, currentUsername]);

  // Deep link from Core (crawler_url): open the detail dialog for ?domain=<d>.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const d = params.get("domain");
    if (d) {
      setDetailDomain(d);
      const url = new URL(window.location.href);
      url.searchParams.delete("domain");
      window.history.replaceState({}, "", url);
    }
  }, []);

  const effectiveConditions = useMemo(
    () => (search ? [...conditions, { field: "domain", op: "starts_with", value: search }] : conditions),
    [conditions, search]);

  const queryParams = useCallback(() => ({
    filters: JSON.stringify(effectiveConditions),
    hidden: showHidden,
    include_rejected: showProblems,
    include_unknown_cms: showProblems || effectiveConditions.length > 0,
    opened,
  }), [effectiveConditions, showHidden, showProblems, opened]);

  const fetchDomains = useCallback(async () => {
    const id = ++requestId.current;
    setLoading(true);
    try {
      const res = await api.get("/domains", { params: { page, per_page: perPage, sort_by: sortBy, sort_order: sortOrder, ...queryParams() } });
      if (id !== requestId.current) return;
      setDomains(res.data.domains);
      setHasMore(res.data.has_more);
    } catch (err) {
      if (id === requestId.current) toast.error(err.response?.data?.detail || "Domains konnten nicht geladen werden");
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [page, perPage, sortBy, sortOrder, queryParams]);

  // Counting is separate and only depends on the filter: paging and sorting never recount.
  const fetchCount = useCallback(async () => {
    const id = ++countId.current;
    setCounting(true);
    try {
      const res = await api.get("/domains/count", { params: queryParams() });
      if (id !== countId.current) return;
      setTotal(res.data.total);
      setTotalCapped(res.data.total_capped);
    } catch {
      if (id === countId.current) { setTotal(null); setTotalCapped(true); }
    } finally {
      if (id === countId.current) setCounting(false);
    }
  }, [queryParams]);

  useEffect(() => { fetchDomains(); }, [fetchDomains, refreshKey]);
  useEffect(() => { fetchCount(); }, [fetchCount, refreshKey]);
  useEffect(() => { setPage(1); setSelected(new Set()); }, [effectiveConditions, showHidden, showProblems, opened]);

  const markOpened = useCallback((domain, openedAt) => {
    setDomains((prev) => prev.map((d) => (d.domain === domain ? { ...d, opened_at: openedAt } : d)));
  }, []);

  // Refresh Core status once per page load for rows that were sent; no polling.
  useEffect(() => {
    if (!coreLinkEnabled || !canWrite) return;
    const withLink = domains.filter((d) => d.core_link);
    if (!withLink.length) return;
    let cancelled = false;
    api.post("/domains/core-status", { domains: withLink.map((d) => d.domain) })
      .then((res) => { if (!cancelled) setCoreLinks((prev) => ({ ...prev, ...res.data })); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [domains, coreLinkEnabled, canWrite]);

  const totalKnown = total !== null && !totalCapped;
  const totalPages = totalKnown ? Math.max(1, Math.min(MAX_PAGES, Math.ceil(total / perPage))) : null;
  const totalLabel = total === null ? "sehr viele" : `${fmt(total)}${totalCapped ? "+" : ""}`;
  const hasRows = domains.length > 0;

  const applyPreset = (preset) => {
    const fieldsInPreset = new Set(preset.conditions.map((c) => c.field));
    setConditions([...conditions.filter((c) => !fieldsInPreset.has(c.field)), ...preset.conditions]);
  };

  const clearAll = () => { setConditions([]); setSearchInput(""); setShowHidden(false); setShowProblems(false); setOpened(""); };

  const handleSort = (field) => {
    if (sortBy === field) setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    else { setSortBy(field); setSortOrder("desc"); }
    setPage(1);
  };

  const SortIcon = ({ field }) => {
    if (sortBy !== field) return <ArrowUpDown className="w-3 h-3 text-zinc-600" />;
    return sortOrder === "asc" ? <ArrowUp className="w-3 h-3 text-blue-400" /> : <ArrowDown className="w-3 h-3 text-blue-400" />;
  };

  const SortHead = ({ field, children, className = "" }) => (
    <TableHead className={`cursor-pointer select-none ${className}`} onClick={() => handleSort(field)}>
      <span className="flex items-center gap-1 text-[10px] uppercase tracking-wider">{children} <SortIcon field={field} /></span>
    </TableHead>
  );

  const toggleSelect = (domain) => setSelected((prev) => {
    const next = new Set(prev);
    next.has(domain) ? next.delete(domain) : next.add(domain);
    return next;
  });
  const toggleSelectAll = () => setSelected(selected.size === domains.length ? new Set() : new Set(domains.map((d) => d.domain)));

  const handleBulkAction = async (bulk, ids = Array.from(selected)) => {
    if (ids.length === 0) return;
    if (bulk === "delete" && !window.confirm(`${ids.length} Domain(s) endgültig löschen?`)) return;
    try {
      await api.post("/domains/bulk-action", { action: bulk, domain_ids: ids });
      toast.success(`${ids.length} Domain(s): ${bulk}`);
      setSelected(new Set());
      fetchDomains();
      fetchCount();
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Aktion fehlgeschlagen");
    }
  };

  const openBulkCore = () => {
    setBulkAssignee(resolveAssignee(coreTeam));
    setBulkCount(selected.size);
    setBulkResult(null);
    setBulkCoreOpen(true);
  };

  const runBulkCore = async () => {
    setBulkSending(true);
    setBulkResult(null);
    try {
      const res = await api.post("/domains/send-to-core/bulk", {
        domains: Array.from(selected),
        assignee_email: bulkAssignee || undefined,
      });
      const { created, exists, failed, results } = res.data;
      // Update the Core badge without a full reload; Core's bulk uses "created" for a new lead.
      const merged = {};
      Object.entries(results || {}).forEach(([domain, info]) => {
        if (info?.status === "created") merged[domain] = { ...info, status: "active" };
        else if (info?.status === "exists") merged[domain] = info;
      });
      setCoreLinks((prev) => ({ ...prev, ...merged }));
      setBulkResult({ created, exists, failed: failed || [] });
      setSelected(new Set());
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "An Core senden fehlgeschlagen");
    } finally {
      setBulkSending(false);
    }
  };

  const handleExport = () => {
    const params = new URLSearchParams();
    Object.entries(queryParams()).forEach(([k, v]) => params.set(k, String(v)));
    window.location.href = exportUrl(params);
  };

  const handleDeleteAll = async () => {
    const confirm = window.prompt('ALLE Domains endgültig löschen? Zum Bestätigen "ALLE DOMAINS LÖSCHEN" eingeben.');
    if (!confirm) return;
    try {
      const res = await api.delete("/domains/all", { data: { confirm } });
      toast.success(`${fmt(res.data.deleted)} Domains gelöscht`);
      fetchDomains();
      fetchCount();
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Löschen fehlgeschlagen");
    }
  };

  const runRecrawl = async () => {
    setBusy(true);
    try {
      const res = await api.post("/domains/recrawl", { conditions: effectiveConditions, include_hidden: showHidden });
      toast.success(`${fmt(res.data.queued)} Domains zum Neu-Crawlen eingeplant. Crawler auf der Crawler-Seite starten.`);
      setAction(null);
      fetchDomains();
      fetchCount();
      refreshNow?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Neu-Crawl fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  const runResearch = async () => {
    setBusy(true);
    try {
      const res = await api.post("/research/control", { action: "start", conditions: effectiveConditions, limit: Number(researchLimit) });
      if (res.data.success) toast.success(res.data.message); else toast.error(res.data.message);
      setAction(null);
      refreshNow?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Deep Research konnte nicht gestartet werden");
    } finally {
      setBusy(false);
    }
  };

  const runRegister = async () => {
    setBusy(true);
    try {
      const res = await api.post("/register/enqueue", { conditions: effectiveConditions, limit: 5000 });
      toast.success(`${fmt(res.data.queued)} von ${fmt(res.data.eligible)} registerpflichtigen Firmen eingereiht (Abarbeitung im Stundentakt).`);
      setAction(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Einreihen fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  const hasFilters = conditions.length > 0 || searchInput || showHidden || showProblems || opened;

  return (
    <div className="stat-card rounded-sm" data-testid="domains-table">
      {/* Filters */}
      <div className="p-3 sm:p-4 border-b border-zinc-800/80 space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="w-full sm:w-64 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input data-testid="domain-search-input" value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
                   placeholder="Domain beginnt mit…" className="pl-10 h-8 bg-zinc-900 border-zinc-800 text-xs" />
          </div>
          <select
            data-testid="opened-filter"
            value={opened}
            onChange={(e) => setOpened(e.target.value)}
            className="h-8 bg-zinc-900 border-zinc-800 text-[11px] rounded-sm border px-2 text-zinc-300"
          >
            <option value="">Von mir geöffnet: egal</option>
            <option value="opened">Von mir geöffnet: geöffnet</option>
            <option value="not_opened">Von mir geöffnet: nicht geöffnet</option>
          </select>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="outline" className="h-8 text-[11px] rounded-sm border-zinc-700 text-zinc-300">
                <Sparkles className="w-3 h-3 mr-1" /> Schnellfilter
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="bg-zinc-900 border-zinc-800">
              <DropdownMenuLabel className="text-[10px] uppercase text-zinc-500">Zu den Filtern hinzufügen</DropdownMenuLabel>
              {PRESETS.map((p) => (
                <DropdownMenuItem key={p.label} onClick={() => applyPreset(p)} className="text-xs">{p.label}</DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <label className="flex items-center gap-1.5 text-[11px] text-zinc-400 cursor-pointer ml-1">
            <Checkbox checked={showProblems} onCheckedChange={(v) => setShowProblems(!!v)} />
            Blockierte/inaktive/Fehler anzeigen
          </label>
          <label className="flex items-center gap-1.5 text-[11px] text-zinc-400 cursor-pointer">
            <Checkbox checked={showHidden} onCheckedChange={(v) => setShowHidden(!!v)} />
            Nur ausgeblendete
          </label>
          {hasFilters && (
            <Button data-testid="clear-filters-btn" variant="ghost" size="sm" onClick={clearAll} className="h-7 text-[11px] text-zinc-400 hover:text-white">
              <X className="w-3 h-3 mr-1" /> Alles zurücksetzen
            </Button>
          )}
        </div>

        <FilterBuilder schema={schema} conditions={conditions} onChange={setConditions} />

        {/* Result bar */}
        <div className="flex items-center gap-2 flex-wrap pt-1">
          <span className="text-xs text-zinc-300">
            {counting ? <Loader2 className="inline w-3.5 h-3.5 animate-spin text-zinc-500" /> : <b className="font-mono">{totalLabel}</b>} Treffer
          </span>
          {selected.size > 0 && canWrite && (
            <div className="flex items-center gap-1.5 pl-2 border-l border-zinc-800">
              <span className="text-[10px] text-zinc-400 font-mono">{selected.size} markiert</span>
              {coreLinkEnabled && (
                <Button size="sm" variant="outline" onClick={openBulkCore} className="h-7 text-[10px] rounded-sm border-zinc-700">
                  <Send className="w-3 h-3 mr-1" /> Send to Core…
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={() => handleBulkAction(showHidden ? "unhide" : "hide")}
                      className="h-7 text-[10px] rounded-sm border-zinc-700">
                {showHidden ? <Eye className="w-3 h-3 mr-1" /> : <EyeOff className="w-3 h-3 mr-1" />}
                {showHidden ? "Einblenden" : "Ausblenden"}
              </Button>
              <Button size="sm" variant="outline" onClick={() => handleBulkAction("recrawl")} className="h-7 text-[10px] rounded-sm border-zinc-700">
                <RefreshCw className="w-3 h-3 mr-1" /> Neu crawlen
              </Button>
              {isAdmin && (
                <Button size="sm" variant="outline" onClick={() => handleBulkAction("delete")}
                        className="h-7 text-[10px] rounded-sm border-red-800 text-red-400 hover:bg-red-500/10">
                  <Trash2 className="w-3 h-3 mr-1" /> Löschen
                </Button>
              )}
            </div>
          )}
          <div className="flex items-center gap-1.5 flex-wrap sm:ml-auto">
            {canWrite && (
              <>
                <Button size="sm" variant="outline" onClick={() => setAction("recrawl")} disabled={!hasRows}
                        className="h-7 text-[10px] rounded-sm border-zinc-700" data-testid="recrawl-filter-btn">
                  <RefreshCw className="w-3 h-3 mr-1" /> Treffer neu crawlen
                </Button>
                <Button size="sm" variant="outline" onClick={() => setAction("research")} disabled={!hasRows || researchActive}
                        className="h-7 text-[10px] rounded-sm border-zinc-700" data-testid="research-filter-btn"
                        title={researchActive ? "Deep Research läuft bereits" : undefined}>
                  <FlaskConical className="w-3 h-3 mr-1" /> Deep Research
                </Button>
                <Button size="sm" variant="outline" onClick={() => setAction("register")} disabled={!hasRows}
                        className="h-7 text-[10px] rounded-sm border-zinc-700" data-testid="register-filter-btn">
                  <Landmark className="w-3 h-3 mr-1" /> Handelsregister
                </Button>
                <Button data-testid="export-csv-btn" size="sm" variant="outline" onClick={handleExport} disabled={!hasRows}
                        className="h-7 text-[10px] rounded-sm border-zinc-700">
                  <Download className="w-3 h-3 mr-1" /> Export
                </Button>
              </>
            )}
            {isAdmin && (
              <Button data-testid="delete-all-btn" size="sm" variant="ghost" onClick={handleDeleteAll}
                      className="h-7 text-[10px] rounded-sm text-red-400/70 hover:text-red-300 hover:bg-red-500/10">
                <Trash2 className="w-3 h-3 mr-1" /> Alle löschen
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className={`overflow-x-auto transition-opacity ${loading && domains.length ? "opacity-60" : ""}`}>
        <Table className="min-w-[1150px]">
          <TableHeader>
            <TableRow className="border-zinc-800/80 hover:bg-transparent">
              <TableHead className="w-8">
                <Checkbox data-testid="select-all-checkbox" checked={domains.length > 0 && selected.size === domains.length} onCheckedChange={toggleSelectAll} />
              </TableHead>
              <SortHead field="domain">Domain</SortHead>
              <SortHead field="lead.score">Score</SortHead>
              <TableHead className="text-[10px] uppercase tracking-wider">Größe</TableHead>
              <SortHead field="cms">CMS</SortHead>
              <SortHead field="cms_major">Version</SortHead>
              <SortHead field="server.php_version">PHP</SortHead>
              <SortHead field="hosting.provider">Hoster</SortHead>
              <SortHead field="company_name">Firma / Inhaber</SortHead>
              <TableHead className="text-[10px] uppercase tracking-wider">Kontakt</TableHead>
              {coreLinkEnabled && <TableHead className="text-[10px] uppercase tracking-wider">Core</TableHead>}
              <SortHead field="crawl_status">Status</SortHead>
              <TableHead className="w-8" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && domains.length === 0 && (
              <TableRow><TableCell colSpan={12} className="text-center py-16"><Loader2 className="w-5 h-5 animate-spin text-zinc-500 mx-auto" /></TableCell></TableRow>
            )}
            {!loading && domains.length === 0 && (
              <TableRow><TableCell colSpan={12} className="text-center py-12 text-zinc-500 text-sm">Keine Domains für diese Filter.</TableCell></TableRow>
            )}
            {domains.map((d) => (
              <TableRow key={d.domain}
                        className={`data-table-row border-zinc-800/50 cursor-pointer${d.opened_at ? " opened" : ""}`}
                        title={d.opened_at ? `Geöffnet ${new Date(d.opened_at).toLocaleString("de-DE")}` : undefined}
                        onClick={() => setDetailDomain(d.domain)}>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <Checkbox data-testid={`select-${d.domain}`} checked={selected.has(d.domain)} onCheckedChange={() => toggleSelect(d.domain)} />
                </TableCell>
                <TableCell className="font-mono text-xs text-white">
                  <div className="flex items-center gap-1.5">
                    <span className="truncate max-w-[200px]">{d.domain}</span>
                    {d.redirect_sources?.length > 0 && (
                      <span className="text-[9px] text-sky-400" title={`Weiterleitungen: ${d.redirect_sources.join(", ")}`}>+{d.redirect_sources.length}</span>
                    )}
                  </div>
                  {d.branch && d.branch !== "Other" && <div className="text-[10px] text-zinc-500 font-sans truncate max-w-[200px]">{d.branch}</div>}
                  {d.opened_at && <div className="text-[9px] text-zinc-600 font-sans">geöffnet {relTime(d.opened_at)}</div>}
                </TableCell>
                <TableCell><LeadScore score={d.lead?.score} /></TableCell>
                <TableCell className="text-[10px] text-zinc-400" title={d.company_size?.reasons?.join(" · ")}>
                  {d.company_size?.class || "–"}
                </TableCell>
                <TableCell>
                  {d.cms && d.cms !== "unknown"
                    ? <Badge className={`text-[10px] ${CMS_COLORS[d.cms] || CMS_COLORS.unknown}`}>{d.cms}</Badge>
                    : <span className="text-zinc-700 text-[10px]">–</span>}
                </TableCell>
                <TableCell className="font-mono text-[10px] text-zinc-400">
                  <div className="flex items-center gap-1">
                    <span>{(d.cms === "Drupal" ? drupalLabel(d) : d.cms_major) || "–"}</span>
                    {d.cms === "Drupal" && <SupportBadge status={d.drupal?.support_status} />}
                  </div>
                </TableCell>
                <TableCell className="font-mono text-[10px] text-zinc-400">
                  <div className="flex items-center gap-1">
                    <span>{d.server?.php_version || "–"}</span>
                    <SupportBadge status={d.server?.php_support_status} />
                  </div>
                </TableCell>
                <TableCell className="text-[10px] text-zinc-400 max-w-[120px] truncate">{d.hosting?.provider || "–"}</TableCell>
                <TableCell className="text-xs text-zinc-300 max-w-[180px]">
                  <div className="truncate">{d.company_name || "–"}</div>
                  {d.owner_name && <div className="text-[10px] text-zinc-500 truncate">{d.owner_name}</div>}
                </TableCell>
                <TableCell className="text-[10px] text-zinc-400 max-w-[170px]" onClick={(e) => e.stopPropagation()}>
                  {d.phone && <div className="font-mono truncate">{d.phone}</div>}
                  {d.email && <div className="truncate">{d.email}</div>}
                  {d.linkedin_company && safeExternalUrl(d.linkedin_company) && (
                    <a href={safeExternalUrl(d.linkedin_company)} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 inline-flex items-center gap-1">
                      <Linkedin className="w-3 h-3" /> LinkedIn
                    </a>
                  )}
                  {!d.phone && !d.email && !d.linkedin_company && "–"}
                </TableCell>
                {coreLinkEnabled && (
                  <TableCell><CoreBadge link={coreLinks[d.domain] || d.core_link} /></TableCell>
                )}
                <TableCell>
                  <Badge className={`text-[9px] ${STATUS_COLORS[d.crawl_status] || STATUS_COLORS.pending}`}>
                    {d.crawl_status === "done" ? "live" : d.crawl_status}
                  </Badge>
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-6 w-6 text-zinc-500 hover:text-white" data-testid={`actions-${d.domain}`}>
                        <MoreHorizontal className="w-3.5 h-3.5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-zinc-900 border-zinc-800">
                      <DropdownMenuItem onClick={() => setDetailDomain(d.domain)}>Details</DropdownMenuItem>
                      {safeExternalUrl(d.final_url) && (
                        <DropdownMenuItem onClick={() => window.open(safeExternalUrl(d.final_url), "_blank", "noopener,noreferrer")}>
                          <ExternalLink className="w-3 h-3 mr-2" /> Website öffnen
                        </DropdownMenuItem>
                      )}
                      {canWrite && <DropdownMenuSeparator className="bg-zinc-800" />}
                      {canWrite && (
                        <DropdownMenuItem onClick={() => handleBulkAction("recrawl", [d.domain])}>
                          <RefreshCw className="w-3 h-3 mr-2" /> Neu crawlen
                        </DropdownMenuItem>
                      )}
                      {canWrite && (
                        <DropdownMenuItem onClick={() => handleBulkAction(d.hidden ? "unhide" : "hide", [d.domain])} className="text-zinc-400">
                          <EyeOff className="w-3 h-3 mr-2" /> {d.hidden ? "Einblenden" : "Ausblenden"}
                        </DropdownMenuItem>
                      )}
                      {isAdmin && (
                        <DropdownMenuItem onClick={() => handleBulkAction("delete", [d.domain])} className="text-red-400">
                          <Trash2 className="w-3 h-3 mr-2" /> Löschen
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {(page > 1 || hasMore) && (
        <div className="p-3 sm:p-4 border-t border-zinc-800/80 flex flex-col sm:flex-row items-center justify-between gap-2">
          <p className="text-xs text-zinc-500 font-mono">
            {totalLabel} Treffer · Seite {page}{totalPages ? `/${totalPages}` : ""}
            {page >= MAX_PAGES && ` (max. ${MAX_PAGES} Seiten – Filter verfeinern oder exportieren)`}
          </p>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" className="h-7 w-7" disabled={page <= 1} onClick={() => setPage(1)}><ChevronsLeft className="w-4 h-4" /></Button>
            <Button variant="ghost" size="icon" className="h-7 w-7" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}><ChevronLeft className="w-4 h-4" /></Button>
            <span className="px-3 text-xs text-zinc-300 font-mono">{page}</span>
            <Button variant="ghost" size="icon" className="h-7 w-7" disabled={!hasMore} onClick={() => setPage((p) => p + 1)}><ChevronRight className="w-4 h-4" /></Button>
            <Button variant="ghost" size="icon" className="h-7 w-7" disabled={!totalPages || page >= totalPages} onClick={() => setPage(totalPages)}><ChevronsRight className="w-4 h-4" /></Button>
          </div>
        </div>
      )}

      {detailDomain && (
        <DomainDetailDialog
          domain={detailDomain}
          onClose={() => setDetailDomain(null)}
          coreLinkEnabled={coreLinkEnabled}
          canWrite={canWrite}
          coreTeam={coreTeam}
          currentUsername={currentUsername}
          lastAssignee={lastAssignee}
          onAssigneeChange={saveAssignee}
          onCoreLinkChanged={() => fetchDomains()}
          onOpened={markOpened}
        />
      )}

      <Dialog open={action === "recrawl"} onOpenChange={(o) => !o && setAction(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Treffer neu crawlen</DialogTitle>
            <DialogDescription className="text-zinc-400 text-xs">
              Plant bis zu <b className="text-white">{totalLabel}</b> Domains mit den aktuellen Filtern neu ein. Blockierte,
              inaktive und fehlerhafte Domains sowie bereits wartende werden übersprungen. Danach den Crawler starten.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setAction(null)}>Abbrechen</Button>
            <Button size="sm" className="bg-blue-600 hover:bg-blue-500 text-white" disabled={busy} onClick={runRecrawl}>
              {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />} Einplanen
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={action === "register"} onOpenChange={(o) => !o && setAction(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Handelsregister für Treffer abfragen</DialogTitle>
            <DialogDescription className="text-zinc-400 text-xs">
              Stellt bis zu 5.000 registerpflichtige deutsche Firmen aus {totalLabel} Treffern vorne in die Warteschlange (österreichische und Schweizer Firmen stehen nicht im deutschen Handelsregister).
              Abgearbeitet wird im Hintergrund mit max. 50 Portal-Aufrufen pro Stunde (≈ 16 Firmen/Stunde).
              Firmen, die im letzten Jahr schon abgefragt wurden, werden übersprungen.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setAction(null)}>Abbrechen</Button>
            <Button size="sm" className="bg-blue-600 hover:bg-blue-500 text-white" disabled={busy} onClick={runRegister}>
              {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Landmark className="w-3 h-3 mr-1" />} Einreihen
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={action === "research"} onOpenChange={(o) => !o && setAction(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Deep Research für Treffer</DialogTitle>
            <DialogDescription className="text-zinc-400 text-xs">
              Recherchiert live Domains aus {totalLabel} Treffern, die noch nicht recherchiert sind: Firmen-Zusammenfassung,
              Impressum, Team-/Karriereseite, Projekt- und Firmengröße. Ein GPT-Aufruf pro Domain.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-xs text-zinc-400">Maximale Anzahl Domains</label>
            <Input type="number" min={1} max={50000} value={researchLimit} onChange={(e) => setResearchLimit(e.target.value)}
                   className="h-8 bg-zinc-950 border-zinc-800 text-sm" />
            <p className="text-[11px] text-zinc-500">
              Geschätzte GPT-Kosten: ca. {(Math.min(Number(researchLimit) || 0, total ?? Infinity) * COST_PER_DOMAIN_USD).toFixed(2)} $
              (≈ {(COST_PER_DOMAIN_USD * 100).toFixed(2)} Cent pro Domain)
            </p>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setAction(null)}>Abbrechen</Button>
            <Button size="sm" className="bg-blue-600 hover:bg-blue-500 text-white" disabled={busy || !(Number(researchLimit) > 0)} onClick={runResearch}>
              {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <FlaskConical className="w-3 h-3 mr-1" />} Starten
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={bulkCoreOpen} onOpenChange={(o) => !o && setBulkCoreOpen(false)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Send to Core</DialogTitle>
            <DialogDescription className="text-zinc-400 text-xs">
              {bulkCount} Domain(s) an Core senden.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-xs text-zinc-400">Team-Mitglied</label>
            <select value={bulkAssignee} onChange={(e) => { setBulkAssignee(e.target.value); saveAssignee(e.target.value); }}
                    className="w-full h-8 bg-zinc-950 border-zinc-800 text-sm rounded-sm border px-2 text-zinc-300">
              {coreTeam.map((m) => (
                <option key={m.email} value={m.email}>{m.name} ({m.email})</option>
              ))}
            </select>
            {bulkResult && (
              <div className="text-xs text-zinc-300 pt-1">
                <p>{bulkResult.created} added, {bulkResult.exists} already in Core, {bulkResult.failed.length} failed</p>
                {bulkResult.failed.length > 0 && (
                  <ul className="text-[11px] text-red-400 mt-1 list-disc list-inside">
                    {bulkResult.failed.map((f) => <li key={f.domain}>{f.domain}: {f.error}</li>)}
                  </ul>
                )}
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setBulkCoreOpen(false)}>Abbrechen</Button>
            <Button size="sm" className="bg-blue-600 hover:bg-blue-500 text-white" disabled={bulkSending || !!bulkResult || bulkCount === 0} onClick={runBulkCore}>
              {bulkSending ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />} Senden
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
